import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Gate } from '../../../global/contexts/auth/auth';
import { embryoStageColor, embryoStageLabel } from '../../../core/constants/transfers';
import { useOpuTransferSessions } from '../../transfers/contexts/transfers';
import PartoForm from '../forms/parto-form';
import AbortForm from '../forms/abort-form';

import './styles/partos.css';

// "Sesión de dx2 confirmados" de una OPU (?opu=): las preñeces CONFIRMED (tras dx,
// aún no born/lost) listas para registrar PARTO o ABORTO. Origen: sesiones TE de la
// OPU (admin-aspiration-sessions: transfers traen pregnancy{id,state} + recipient).
export default function PartosWorkspace() {
    const [params] = useSearchParams();
    const opuId = params.get('opu');
    const [farm, setFarm] = useState('');
    const [parto, setParto] = useState(null); // { pregnancyId, recipientLabel, farmId }
    const [abort, setAbort] = useState(null); // { pregnancyId, recipientId, recipientLabel }

    const { data: opuData, isLoading, isFetching, error } = useOpuTransferSessions(opuId, { enabled: !!opuId });

    const rows = useMemo(() => {
        const out = [];
        (opuData?.transfer_sessions || []).forEach((s) => {
            (s.transfers || []).forEach((t) => {
                if (t.pregnancy?.state !== 'confirmed') return;
                out.push({ pregnancyId: t.pregnancy.id, recipient: t.recipient, embryo: t.embryo, farm: s.farm, teCode: s.code });
            });
        });
        return out.sort((a, b) => String(a.recipient?.number || '').localeCompare(String(b.recipient?.number || '')));
    }, [opuData]);

    const farmOptions = useMemo(() => {
        const m = new Map();
        rows.forEach((r) => { if (r.farm?.id != null) m.set(r.farm.id, r.farm.name); });
        return [...m.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }, [rows]);

    const view = farm ? rows.filter((r) => String(r.farm?.id) === farm) : rows;
    const recipientLabel = (r) => `${r.recipient?.number || '—'}${r.recipient?.name ? ` · ${r.recipient.name}` : ''}`;

    if (!opuId) {
        return <div className="partos"><div className="partos__state">Abrí los partos desde una sesión OPU (vista de la OPU → “Partos”).</div></div>;
    }

    return (
        <div className="partos">
            <header className="partos__header">
                <div className="partos__heading">
                    <h1 className="partos__title">Partos</h1>
                    {opuData?.opu?.code && <span className="partos__opu">{opuData.opu.code}</span>}
                    <span className="partos__count">{view.length}</span>
                </div>
                <p className="partos__subtitle">Preñeces confirmadas (tras dx2) listas para registrar parto o aborto.</p>
            </header>

            <div className="partos__toolbar">
                <label className="partos__field">
                    <span className="partos__label">Finca</span>
                    <select value={farm} onChange={(e) => setFarm(e.target.value)}>
                        <option value="">Todas las fincas</option>
                        {farmOptions.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                    </select>
                </label>
            </div>

            {error && <div className="partos__error">{error.message || 'No se pudieron cargar las preñeces'}</div>}

            {isLoading ? (
                <div className="partos__state">Cargando…</div>
            ) : !view.length ? (
                <div className="partos__state">No hay preñeces confirmadas para registrar parto.</div>
            ) : (
                <div className={`partos__card ${isFetching ? 'partos__card--busy' : ''}`}>
                    <table className="partos__table">
                        <thead>
                            <tr><th>Receptora</th><th>Embrión</th><th>Finca</th><th>T.E.</th><th aria-label="Acciones" /></tr>
                        </thead>
                        <tbody>
                            {view.map((r) => (
                                <tr key={r.pregnancyId} className="partos__row">
                                    <td>
                                        <span className="partos__recipient">{r.recipient?.number || '—'}</span>
                                        {r.recipient?.name && <span className="partos__recipient-name">{r.recipient.name}</span>}
                                    </td>
                                    <td>
                                        {r.embryo?.code ? (
                                            <span className="partos__emb">
                                                {r.embryo.code}
                                                {r.embryo.stage && <span className="partos__stage" style={{ '--stage': embryoStageColor(r.embryo.stage) }}>{embryoStageLabel(r.embryo.stage)}</span>}
                                            </span>
                                        ) : '—'}
                                    </td>
                                    <td>{r.farm?.name || '—'}</td>
                                    <td><span className="partos__te">{r.teCode || '—'}</span></td>
                                    <td className="partos__actions">
                                        <Gate domain="births" action="write">
                                            <button type="button" className="partos__btn partos__btn--primary" onClick={() => setParto({ pregnancyId: r.pregnancyId, recipientLabel: recipientLabel(r), farmId: r.farm?.id })}>Parto</button>
                                        </Gate>
                                        <Gate domain="medical_events" action="write">
                                            <button type="button" className="partos__btn partos__btn--ghost" onClick={() => setAbort({ pregnancyId: r.pregnancyId, recipientId: r.recipient?.id, recipientLabel: recipientLabel(r) })}>Aborto</button>
                                        </Gate>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {parto && <PartoForm pregnancyId={parto.pregnancyId} recipientLabel={parto.recipientLabel} defaultFarmId={parto.farmId} onClose={() => setParto(null)} onSaved={() => setParto(null)} />}
            {abort && <AbortForm pregnancyId={abort.pregnancyId} recipientId={abort.recipientId} recipientLabel={abort.recipientLabel} onClose={() => setAbort(null)} onSaved={() => setAbort(null)} />}
        </div>
    );
}
